import styled from 'styled-components'

import Badge from 'productTemplate/components/Badge'
import Divider from 'shared/components/Divider'
import RadioButton from 'checkout/components/RadioButton'
import RadioGroup from 'checkout/components/RadioGroup'
import Spinner from 'shared/components/Spinner'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { setIsFastestToYouAvailable } from 'store/checkoutSlice'
import { t } from 'localization'
import { useAppDispatch } from 'store/hooks'
import { useEffect, useState } from 'react'
import { useGetPricesQuery } from 'api/productVariantsApi'
import { useOrderSummaryContext } from 'checkout/containers/OrderSummaryContextProvider'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'

const TP = 'checkout.panels.OrderSummary.OrderSummaryOptions'

type OrderOptions = 'fastestToYou' | 'lowestPrice'

export const OrderSummaryOptions = () => {
  const { changeOrderOptionRTK, order } = useOrderSummaryContext()
  const { country } = useShoppingRegionContext()
  const dispatch = useAppDispatch()
  const [selectedOption, setSelectedOption] = useState<OrderOptions>(
    order?.isInstantShip ? 'fastestToYou' : 'lowestPrice',
  )

  const {
    data: prices,
    isLoading,
    isSuccess,
  } = useGetPricesQuery({
    countryCode: country,
    productTemplateId: order?.product?.productTemplate?.id,
    size: order?.product?.sizeOption?.presentation,
  })

  const handleClickOption = async (option: OrderOptions) => {
    setSelectedOption(option)
    const isFty = option === 'fastestToYou'
    const priceCents = isFty
      ? prices?.[0]?.instantShipLowestPriceCents?.amount
      : prices?.[0]?.lowestPriceCents?.amount
    const orderDetails = {
      expectedPriceCents: priceCents,
      instantShip: isFty,
    }
    sendTrackingEvent(isFty ? 'FASTEST_TO_YOU_SELECTION_TAP' : 'LOWEST_PRICE_SELECTION_TAP', {
      fastest_price: `${prices?.[0]?.instantShipLowestPriceCents?.amount}`,
      lowest_price: `${prices?.[0]?.lowestPriceCents?.amount}`,
      order_number: `${order?.number || null}`,
      product_id: `${order?.product?.id || null}`,
      product_template_id: `${order?.product?.productTemplate?.id || null}`,
      size: order?.product?.sizeOption?.presentation,
    })
    await changeOrderOptionRTK(orderDetails)
  }

  useEffect(() => {
    if (isSuccess) {
      sendTrackingEvent('FASTEST_TO_YOU_VIEW', {
        fastest_price: `${prices?.[0]?.instantShipLowestPriceCents?.amount}`,
        lowest_price: `${prices?.[0]?.lowestPriceCents?.amount}`,
        order_number: `${order?.number || null}`,
        product_id: `${order?.product?.id || null}`,
        product_template_id: `${order?.product?.productTemplate?.id || null}`,
        size: order?.product?.sizeOption?.presentation,
      })
      dispatch(setIsFastestToYouAvailable(!!prices?.[0]?.instantShipLowestPriceCents?.amount))
    }
  }, [isSuccess])

  if (isSuccess && !prices?.[0]?.instantShipLowestPriceCents?.amount) {
    return <Divider />
  }

  return (
    <Container>
      {isLoading && <Spinner showSpinner />}
      {isSuccess && (
        <RadioOptions name="instantShipOptions">
          <Option
            checked={selectedOption === 'lowestPrice'}
            key="lowestPrice"
            onRadioSelect={() => handleClickOption('lowestPrice')}
            qaAttr="lowest-price-radio-option"
            subtitle={t(`${TP}.lowestPriceDelivery`, 'Ships to us first for verification')}
            title={
              <Badge text={t(`${TP}.lowestPriceBadge`, 'Lowest Price')} badgeType="primary1" />
            }
            value="lowestPrice"
          />
          <Option
            checked={selectedOption === 'fastestToYou'}
            key="fastestToYou"
            onRadioSelect={() => handleClickOption('fastestToYou')}
            qaAttr="fastest-to-you-radio-option"
            subtitle={t(`${TP}.fastestToYouDelivery`, 'Estimated arrival in 1-5 business days')}
            title={
              <Badge text={t(`${TP}.fastestToYouBadge`, 'Fastest to you')} badgeType="primary2" />
            }
            value="fastestToYou"
          />
        </RadioOptions>
      )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  margin-bottom: 30px;
  margin-top: 30px;
`
const Option = styled(RadioButton)`
  height: 92px;
  margin-left: 6px;
  margin-right: 6px;
  padding: 15px;
  width: 50%;

  ${RadioButton.Subtitle} {
    font-size: 12px;
    font-weight: 400;
    line-height: 12px;
    margin-top: 36px;
    text-align: left;
  }
`
const RadioOptions = styled(RadioGroup)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;

  ${/* sc-selector */ Option}:first-child {
    margin-left: 0px;
  }
  ${/* sc-selector */ Option}:last-child {
    margin-right: 0px;
  }
`
